import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { builtInDictionaries } from '../builtInDictionaries';

interface Assignment {
  assignment_id: number;
  assignment_name: string;
  assignment_description: string;
  requirements: any;
  due_date: string;
  status: string;
  assigned_at: string;
  class_name: string;
  teacher_name: string;
  game_count: number;
  attempt_count: number;
  best_score: number;
}

interface AssignmentDetails {
  id: number;
  name: string;
  description: string;
  requirements: any;
  due_date: string;
  class_name: string;
  teacher_name: string;
  games: Array<{
    id: number;
    game_order: number;
    dictionary_id?: number;
    built_in_game_id?: string;
    pair_count?: number;
    dictionary_name?: string;
    dictionary_content?: any;
    matching_mode?: 'one-to-one' | 'category';
    dictionary_matching_mode?: 'one-to-one' | 'category';
  }>;
}

interface StudentAssignmentsProps {
  onClose: () => void;
  onStartGame: (dictionary: any, assignmentId?: number, gameId?: number, pairCount?: number) => void;
}

const StudentAssignments: React.FC<StudentAssignmentsProps> = ({ onClose, onStartGame }) => {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [selectedAssignment, setSelectedAssignment] = useState<AssignmentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAssignments();
  }, []);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      const data = await apiService.getMyAssignments();
      if (data.success) {
        setAssignments(data.assignments);
      } else {
        setError(data.error || 'Failed to load assignments');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const loadAssignmentDetails = async (assignmentId: number) => {
    try {
      setLoading(true);
      const data = await apiService.getAssignmentDetailsForStudent(assignmentId);
      if (data.success) {
        setSelectedAssignment(data.assignment);
      } else {
        setError(data.error || 'Failed to load assignment details');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load assignment details');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAssignment = async (assignment: Assignment) => {
    await loadAssignmentDetails(assignment.assignment_id);
  };

  const handleStartGame = (game: AssignmentDetails['games'][0]) => {
    if (!selectedAssignment) return;

    let dictionary: any;

    if (game.built_in_game_id) {
      // Find built-in game from builtInDictionaries
      const builtInGame = builtInDictionaries.find(dict => dict.id === game.built_in_game_id);
      if (builtInGame) {
        dictionary = {
          name: builtInGame.name,
          content: builtInGame.data
        };
      }
    } else if (game.dictionary_content) {
      // Saved dictionary
      dictionary = {
        name: game.dictionary_name,
        content: game.dictionary_content
      };
    }

    if (dictionary) {
      // Pass the required pair count from the assignment (undefined if not set - will show slider)
      console.log('🎮 Starting assignment game:', { gameId: game.id, pairCount: game.pair_count, gameData: game });
      onStartGame(dictionary, selectedAssignment.id, game.id, game.pair_count);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { text: string; className: string }> = {
      completed: { text: '✅ Completed', className: 'assignment-status-completed' },
      in_progress: { text: '🔄 In Progress', className: 'assignment-status-in-progress' },
      pending: { text: '⏳ Not Started', className: 'assignment-status-pending' },
    };
    return badges[status] || { text: status, className: '' };
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'No deadline';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isOverdue = (dateStr: string, status: string) => {
    if (!dateStr || status === 'completed') return false;
    return new Date(dateStr) < new Date();
  };

  const groupAssignmentsByTeacher = () => {
    const grouped: Record<string, Assignment[]> = {};
    assignments.forEach(assignment => {
      const key = assignment.teacher_name;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(assignment);
    });
    return grouped;
  };

  if (loading && assignments.length === 0) {
    return (
      <div className="student-assignments">
        <div className="loading-message">Loading assignments...</div>
      </div>
    );
  }

  return (
    <div className="student-assignments">
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* Assignments List View */}
      {!selectedAssignment && (
        <>

          {assignments.length === 0 ? (
            <div className="empty-state">
              <p>You don't have any assignments yet.</p>
              <p>Your teacher will assign games for you to practice!</p>
            </div>
          ) : (
            <div className="assignments-by-teacher">
              {Object.entries(groupAssignmentsByTeacher()).map(([teacherName, teacherAssignments]) => (
                <div key={teacherName} className="teacher-group">
                  <h3 className="teacher-group-header">👨‍🏫 {teacherName}</h3>

                  <div className="assignments-list">
                    {teacherAssignments.map(assignment => {
                      const badge = getStatusBadge(assignment.status);
                      const overdue = isOverdue(assignment.due_date, assignment.status);

                      return (
                        <div
                          key={assignment.assignment_id}
                          className={`assignment-item ${overdue ? 'overdue' : ''}`}
                        >
                          <div className="assignment-item-header">
                            <div>
                              <h4>{assignment.assignment_name}</h4>
                              <div className="assignment-meta">
                                <span className="class-tag">📚 {assignment.class_name}</span>
                                <span className={`status-tag ${badge.className}`}>
                                  {badge.text}
                                </span>
                              </div>
                            </div>
                          </div>

                          {assignment.assignment_description && (
                            <p className="assignment-item-description">
                              {assignment.assignment_description}
                            </p>
                          )}

                          <div className="assignment-item-stats">
                            <div className="stat">
                              <span className="stat-icon">🎮</span>
                              <span>{assignment.game_count} game{assignment.game_count !== 1 ? 's' : ''}</span>
                            </div>
                            <div className="stat">
                              <span className="stat-icon">🎯</span>
                              <span>{assignment.attempt_count} attempt{assignment.attempt_count !== 1 ? 's' : ''}</span>
                            </div>
                            {assignment.best_score > 0 && (
                              <div className="stat">
                                <span className="stat-icon">🏆</span>
                                <span>Best: {assignment.best_score}</span>
                              </div>
                            )}
                          </div>

                          <div className={`assignment-due ${overdue ? 'overdue' : ''}`}>
                            📅 {formatDate(assignment.due_date)}
                            {overdue && ' (OVERDUE!)'}
                          </div>

                          <button
                            onClick={() => handleViewAssignment(assignment)}
                            className="view-assignment-btn"
                          >
                            View & Play →
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Assignment Detail View */}
      {selectedAssignment && (
        <div className="assignment-detail">
          <div className="assignment-detail-header">
            <button
              onClick={() => setSelectedAssignment(null)}
              className="back-btn"
            >
              ← Back to All Assignments
            </button>
            <button onClick={onClose} className="close-button">✕ Close</button>
          </div>

          <div className="assignment-detail-content">
            <h2>{selectedAssignment.name}</h2>

            <div className="assignment-detail-meta">
              <span className="meta-item">
                👨‍🏫 Teacher: {selectedAssignment.teacher_name}
              </span>
              <span className="meta-item">
                📚 Class: {selectedAssignment.class_name}
              </span>
              <span className="meta-item">
                📅 Due: {formatDate(selectedAssignment.due_date)}
              </span>
            </div>

            {selectedAssignment.description && (
              <div className="assignment-detail-description">
                <h3>Description</h3>
                <p>{selectedAssignment.description}</p>
              </div>
            )}

            {selectedAssignment.requirements && (
              <div className="assignment-requirements">
                <h3>Requirements</h3>
                <div className="requirements-content">
                  {typeof selectedAssignment.requirements === 'string'
                    ? selectedAssignment.requirements
                    : JSON.stringify(selectedAssignment.requirements, null, 2)}
                </div>
              </div>
            )}

            <div className="assignment-games-section">
              <h3>Games to Complete ({selectedAssignment.games.length})</h3>
              <p className="games-note">
                💡 You can play each game as many times as you want. Your teacher will see all your attempts!
              </p>

              <div className="games-list">
                {selectedAssignment.games.map((game, index) => {
                  // Detect if game is category mode
                  const isCategoryMode = game.matching_mode === 'category' ||
                    game.dictionary_matching_mode === 'category' ||
                    game.dictionary_content?.metadata?.matchingMode === 'category';

                  return (
                    <div key={game.id} className="game-item">
                      <div className="game-item-info">
                        <span className="game-number">#{index + 1}</span>
                        <span className="game-name">
                          {game.built_in_game_id
                            ? game.built_in_game_id.charAt(0).toUpperCase() + game.built_in_game_id.slice(1)
                            : game.dictionary_name}
                        </span>
                        {isCategoryMode && (
                          <span className="game-mode-badge category-mode">📂 Category</span>
                        )}
                        {game.pair_count && (
                          <span className="game-pair-requirement">🎯 {game.pair_count} pairs required</span>
                        )}
                        {game.built_in_game_id && (
                          <span className="game-type-badge">Built-in Game</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleStartGame(game)}
                        className="start-game-btn"
                      >
                        ▶ Play
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentAssignments;
